import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);

  if (!user) return null;
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profil uppdaterad");
    } catch {
      toast.error("Kunde inte spara");
    } finally { setSaving(false); }
  };

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Kontoinställningar</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
        Profil
      </h1>

      <div className="mt-8 bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-700 text-white flex items-center justify-center text-xl font-semibold">
            {user.name?.[0]}
          </div>
          <div>
            <div className="font-medium text-zinc-900">{user.name}</div>
            <div className="text-sm text-zinc-500">{user.email}</div>
            <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mt-1">
              {user.role === "admin" ? "Handläggare" : "Privatperson"}
            </div>
          </div>
        </div>
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Namn</label>
            <input value={form.name} onChange={f("name")} data-testid="profile-name"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Telefon</label>
            <input value={form.phone} onChange={f("phone")} data-testid="profile-phone"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          </div>
          <button type="submit" disabled={saving} data-testid="profile-save"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Spara ändringar</>}
          </button>
        </form>
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-zinc-900">Logga ut</h3>
            <p className="text-sm text-zinc-500">Avsluta din nuvarande session.</p>
          </div>
          <button onClick={doLogout} data-testid="profile-logout"
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 px-4 py-2 rounded-sm text-sm font-medium hover:bg-zinc-100">
            <LogOut className="w-4 h-4" /> Logga ut
          </button>
        </div>
      </div>
    </div>
  );
}
